import {
  GenerateLassieMiniRequest,
  GenerateLassieRequest,
  GenerateShuttleRequest,
  LassieParams,
  LassieMiniParams,
  LoadType,
  ShuttleRackParams,
  GoodsToPersonsExtension,
  HikStorageParams,
  GenerateHikStorageRequest,
} from '@thive/fddm-types';
import { v4 as uuid } from 'uuid';

import { assertNever } from '@/helpers/utils';
import { NumUtils } from '@/modules/common/utils';
import { mmToMetersLoadType } from '@/modules/loadCarriers/helpers';

import { generateTemplateGroupName } from '@/modules/floorplanDesign/helpers/name';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';

interface LassieTemplateCreationParameters {
  params: LassieParams;
  groupName: string;
  toteLoadType: LoadType;
  transportUvtFileRef: string;
  pickingUvtFileRef: string;
  hourlyOrderCounts: [number, ...number[]];
  flow: GoodsToPersonsExtension;
}

interface LassieMiniTemplateCreationParameters {
  params: LassieMiniParams;
  groupName: string;
  toteLoadType: LoadType;
  transportUvtFileRef: string;
  pickingUvtFileRef: string;
}

interface ShuttleTemplateCreationParameters {
  params: ShuttleRackParams;
  groupName: string;
  carrierLoadType: LoadType;
  shuttleLoadType: LoadType;
  shuttleUvtFileRef: string;
  forkLiftUvtFileRef: string;
}

interface HikTemplateCreationParameters {
  params: HikStorageParams;
  groupName: string;
  toteLoadType: LoadType;
  transportUvtFileRef: string;
  pickingUvtFileRef: string;
}

interface InitialLassieTemplateCreationParameters {
  mousePos: { x: number; y: number };
  toteLoadType: LoadType;
  transportVehicleUvtFileRef: string;
  pickingVehicleUvtFileRef: string;
}

interface InitialLassieMiniTemplateCreationParameters {
  mousePos: { x: number; y: number };
  toteLoadType: LoadType;
  transportVehicleUvtFileRef: string;
  pickingVehicleUvtFileRef: string;
}

interface InitialShuttleTemplateCreationParameters {
  mousePos: { x: number; y: number };
  carrierLoad: LoadType;
  shuttleLoad: LoadType;
  shuttleUvtFileRef: string;
  forkLiftUvtFileRef: string;
}

interface InitialHikTemplateCreationParameters {
  mousePos: { x: number; y: number };
  toteLoadType: LoadType;
  transportVehicleUvtFileRef: string;
  pickingVehicleUvtFileRef: string;
}

type buildTemplateParameters =
  | {
      templateType: 'SHUTTLE';
      creationParameters: ShuttleTemplateCreationParameters;
    }
  | {
      templateType: 'LASSIE';
      creationParameters: LassieTemplateCreationParameters;
    }
  | {
      templateType: 'LASSIE_MINI';
      creationParameters: LassieMiniTemplateCreationParameters;
    }
  | {
      templateType: 'HIK';
      creationParameters: HikTemplateCreationParameters;
    };

type buildInitialTemplateParameters =
  | {
      templateType: 'SHUTTLE';
      creationParameters: InitialShuttleTemplateCreationParameters;
    }
  | {
      templateType: 'LASSIE';
      creationParameters: InitialLassieTemplateCreationParameters;
    }
  | {
      templateType: 'LASSIE_MINI';
      creationParameters: InitialLassieMiniTemplateCreationParameters;
    }
  | {
      templateType: 'HIK';
      creationParameters: InitialHikTemplateCreationParameters;
    };

interface IFactory {
  buildInitialTemplateRequest(
    buildTemplateParameters: buildInitialTemplateParameters,
  ): GenerateLassieRequest | GenerateShuttleRequest | GenerateLassieMiniRequest;
  buildTemplateRequest(
    parameters: buildTemplateParameters,
  ): GenerateLassieRequest | GenerateShuttleRequest | GenerateLassieMiniRequest;
}

/**
 * @description Factory used for building the template requests
 */
class Factory implements IFactory {
  /* Overloads to get type safety */
  /** @description Initial requests used for building the initial template */
  buildInitialTemplateRequest(parameters: {
    templateType: 'LASSIE';
    creationParameters: InitialLassieTemplateCreationParameters;
  }): GenerateLassieRequest;
  buildInitialTemplateRequest(parameters: {
    templateType: 'SHUTTLE';
    creationParameters: InitialShuttleTemplateCreationParameters;
  }): GenerateShuttleRequest;
  buildInitialTemplateRequest(parameters: {
    templateType: 'LASSIE_MINI';
    creationParameters: InitialLassieMiniTemplateCreationParameters;
  }): GenerateLassieMiniRequest;
  buildInitialTemplateRequest(parameters: {
    templateType: 'HIK';
    creationParameters: InitialHikTemplateCreationParameters;
  }): GenerateHikStorageRequest;

  buildInitialTemplateRequest(buildTemplateParameters: buildInitialTemplateParameters) {
    const { templateType, creationParameters } = buildTemplateParameters;
    switch (templateType) {
      case 'LASSIE':
        return this.buildInitialLassieRequest(creationParameters);
      case 'LASSIE_MINI':
        return this.buildInitialLassieMiniRequest(creationParameters);
      case 'SHUTTLE':
        return this.buildInitialShuttleRequest(creationParameters);
      case 'HIK':
        return this.buildInitialHikRequest(creationParameters);
      default:
        assertNever(templateType);
    }
  }

  private buildInitialLassieRequest(
    params: InitialLassieTemplateCreationParameters,
  ): GenerateLassieRequest {
    const { getPreference } = useFloorplanDesignStore.getState();
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: generateTemplateGroupName('lassie'),
      params: {
        numAisles: getPreference('template.lassie.numAisles'),
        numBays: getPreference('template.lassie.numBays'),
        numShelves: getPreference('template.lassie.numShelves'),
        sprinklerShelves: [],
        workstations: [],
        centerX: Math.round(NumUtils.mmToMeters(params.mousePos.x)),
        centerY: Math.round(NumUtils.mmToMeters(params.mousePos.y)),
        angle: 0,
      },
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportVehicleUvtFileRef,
      pickingUvtFileRef: params.pickingVehicleUvtFileRef,
      hourlyOrderCounts: [getPreference('flow.hourlyOrderCounts/goodsToPersons')],
    };
  }

  private buildInitialLassieMiniRequest(
    params: InitialLassieMiniTemplateCreationParameters,
  ): GenerateLassieMiniRequest {
    const { getPreference } = useFloorplanDesignStore.getState();
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: generateTemplateGroupName('lassie_mini'),
      params: {
        numAisles: getPreference('template.lassieMini.numAisles'),
        numBays: getPreference('template.lassieMini.numBays'),
        numShelves: getPreference('template.lassieMini.numShelves'),
        centerX: Math.round(NumUtils.mmToMeters(params.mousePos.x)),
        centerY: Math.round(NumUtils.mmToMeters(params.mousePos.y)),
        angle: 0,
      },
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportVehicleUvtFileRef,
      pickingUvtFileRef: params.pickingVehicleUvtFileRef,
    };
  }

  private buildInitialShuttleRequest(
    params: InitialShuttleTemplateCreationParameters,
  ): GenerateShuttleRequest {
    const { getPreference } = useFloorplanDesignStore.getState();
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: generateTemplateGroupName('shuttle'),
      params: {
        numColumns: getPreference('template.shuttle.numColumns'),
        numLevels: getPreference('template.shuttle.numLevels'),
        numDeep: getPreference('template.shuttle.numDeep'),
        entrySide: getPreference('template.shuttle.entrySide'),
        centerX: Math.round(NumUtils.mmToMeters(params.mousePos.x)),
        centerY: Math.round(NumUtils.mmToMeters(params.mousePos.y)),
        angle: 0,
      },
      carrierLoadType: mmToMetersLoadType(params.carrierLoad),
      shuttleLoadType: mmToMetersLoadType(params.shuttleLoad),
      shuttleUvtFileRef: params.shuttleUvtFileRef,
      forkLiftUvtFileRef: params.forkLiftUvtFileRef,
    };
  }

  private buildInitialHikRequest(
    params: InitialHikTemplateCreationParameters,
  ): GenerateHikStorageRequest {
    const { getPreference } = useFloorplanDesignStore.getState();
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: generateTemplateGroupName('hik'),
      params: {
        numAisles: getPreference('template.hik.numAisles'),
        numBays: getPreference('template.hik.numBays'),
        numShelves: getPreference('template.hik.numShelves'),
        crossAislesAfterBays: [],
        centerX: Math.round(NumUtils.mmToMeters(params.mousePos.x)),
        centerY: Math.round(NumUtils.mmToMeters(params.mousePos.y)),
        angle: 0.0,
      },
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportVehicleUvtFileRef,
      pickingUvtFileRef: params.pickingVehicleUvtFileRef,
    };
  }

  /** @description Requests used for building the template with required parameters */
  buildTemplateRequest(parameters: {
    templateType: 'LASSIE';
    creationParameters: LassieTemplateCreationParameters;
  }): GenerateLassieRequest;
  buildTemplateRequest(parameters: {
    templateType: 'SHUTTLE';
    creationParameters: ShuttleTemplateCreationParameters;
  }): GenerateShuttleRequest;
  buildTemplateRequest(parameters: {
    templateType: 'LASSIE_MINI';
    creationParameters: LassieMiniTemplateCreationParameters;
  }): GenerateLassieMiniRequest;
  buildTemplateRequest(parameters: {
    templateType: 'HIK';
    creationParameters: HikTemplateCreationParameters;
  }): GenerateHikStorageRequest;

  buildTemplateRequest(buildTemplateParameters: buildTemplateParameters) {
    const { templateType, creationParameters } = buildTemplateParameters;
    switch (templateType) {
      case 'SHUTTLE':
        return this.buildShuttleRequest(creationParameters);
      case 'LASSIE':
        return this.buildLassieRequest(creationParameters);
      case 'LASSIE_MINI':
        return this.buildLassieMiniRequest(creationParameters);
      case 'HIK':
        return this.buildHikRequest(creationParameters);
      default:
        assertNever(templateType);
    }
  }

  private buildLassieRequest(params: LassieTemplateCreationParameters): GenerateLassieRequest {
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: params.groupName,
      params: params.params,
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportUvtFileRef,
      pickingUvtFileRef: params.pickingUvtFileRef,
      hourlyOrderCounts: params.hourlyOrderCounts,
      flow: params.flow,
    };
  }

  private buildLassieMiniRequest(
    params: LassieMiniTemplateCreationParameters,
  ): GenerateLassieMiniRequest {
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: params.groupName,
      params: params.params,
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportUvtFileRef,
      pickingUvtFileRef: params.pickingUvtFileRef,
    };
  }

  private buildShuttleRequest(params: ShuttleTemplateCreationParameters): GenerateShuttleRequest {
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: params.groupName,
      params: params.params,
      carrierLoadType: mmToMetersLoadType(params.carrierLoadType),
      shuttleLoadType: mmToMetersLoadType(params.shuttleLoadType),
      shuttleUvtFileRef: params.shuttleUvtFileRef,
      forkLiftUvtFileRef: params.forkLiftUvtFileRef,
    };
  }

  private buildHikRequest(params: HikTemplateCreationParameters): GenerateHikStorageRequest {
    return {
      jobId: uuid(),
      messageTs: new Date().toISOString(),
      metaData: useFloorplanDesignStore.getState().metaData,
      groupName: params.groupName,
      params: params.params,
      toteLoadType: mmToMetersLoadType(params.toteLoadType),
      transportUvtFileRef: params.transportUvtFileRef,
      pickingUvtFileRef: params.pickingUvtFileRef,
    };
  }
}

export const factory = new Factory();
